/* ==========================================================================
   B2B Order Builder — the "Build Order" step
   Browse the catalogue, tick styles into one or more shipments, then hand a
   shipment off to the size grid to enter quantities.

   WORKBOOK STORAGE FORMAT (also read by b2b-quick-order.js — keep in sync)
   ------------------------------------------------------------------------
   localStorage['b2bqo:workbook:v1:<customerId>'] = {
     activeId: 's-abc123',
     shipments: [{
       id:         's-abc123',
       name:       'Shipment 1',
       shipDate:   '2027-02-01',   // yyyy-mm-dd, or ''
       cancelDate: '',
       po:         '',
       styles:     ['10283321032985', ...],  // Shopify product ids, as strings
       submittedAt:  null,          // ISO string once added to the trade cart
       submittedSkus: [],           // what went in, so the cart can be rechecked
       orderedAt:  null,            // ISO string once a real order matched it
       orderName:  '',              // e.g. '#1042' — the proof it was ordered
       orderedSession: ''           // the visit that showed it; retired after
     }]
   }

   Storage is per browser, not per account — SparkLayer's SDK exposes no list
   API we can write to, so a buyer who switches machines starts fresh. Worth
   knowing before anyone builds a season plan on their laptop and opens it on
   an iPad.
   ========================================================================== */
(function () {
  'use strict';

  var Workbook = {
    key: function (customerId) {
      return 'b2bqo:workbook:v1:' + (customerId || 'anon');
    },

    blank: function () {
      var s = this.newShipment('Shipment 1');
      return { activeId: s.id, shipments: [s] };
    },

    newShipment: function (name) {
      return {
        id: 's-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        name: name,
        shipDate: '',
        cancelDate: '',
        po: '',
        styles: [],
        submittedAt: null,
        submittedSkus: [],
        orderedAt: null,
        orderName: '',
        orderedSession: ''
      };
    },

    /* Numbering skips what is already on screen rather than counting it, so
       retiring "Shipment 1" cannot hand its name to the next one. */
    nextName: function (wb) {
      var highest = 0;
      wb.shipments.forEach(function (s) {
        var name = s.name || '';
        if (name.indexOf('Shipment ') !== 0) return;
        var n = parseInt(name.slice(9), 10);
        if (n > highest) highest = n;
      });
      return 'Shipment ' + (Math.max(highest, wb.shipments.length) + 1);
    },

    // Storage can throw outright in private mode, so every read and write is
    // guarded and falls back to a fresh in-memory workbook.
    load: function (customerId) {
      try {
        var raw = window.localStorage.getItem(this.key(customerId));
        if (!raw) return this.blank();
        var wb = JSON.parse(raw);
        if (!wb || !Array.isArray(wb.shipments) || !wb.shipments.length) return this.blank();
        return wb;
      } catch (e) {
        return this.blank();
      }
    },

    /* The size grid needs to find this workbook without knowing which customer
       is logged in, so every save also drops a pointer at a fixed key. That
       keeps the customer id out of the other page entirely — no second source
       of truth to drift. */
    POINTER: 'b2bqo:workbook:current',

    save: function (customerId, wb) {
      try {
        var key = this.key(customerId);
        window.localStorage.setItem(key, JSON.stringify(wb));
        window.localStorage.setItem(this.POINTER, key);
      } catch (e) { /* quota or private mode — the session still works */ }
    },

    active: function (wb) {
      var found = null;
      wb.shipments.forEach(function (s) { if (s.id === wb.activeId) found = s; });
      return found || wb.shipments[0];
    },

    find: function (wb, id) {
      var found = null;
      wb.shipments.forEach(function (s) { if (s.id === id) found = s; });
      return found;
    }
  };

  window.B2BWorkbook = Workbook;

  /* One id per browsing session, so Build Order and the size grid can be
     navigated between without a completed shipment vanishing mid-visit.
     sessionStorage is per tab and dies with it, which is exactly the lifetime
     "this visit" should mean. */
  function visitId() {
    var fresh = 'v-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    try {
      var id = window.sessionStorage.getItem('b2bqo:visit');
      if (!id) {
        id = fresh;
        window.sessionStorage.setItem('b2bqo:visit', id);
      }
      return id;
    } catch (e) {
      // Private mode has no stable id, so a completed shipment clears on the
      // next page load rather than the next visit. Never mid-page, though.
      return fresh;
    }
  }

  /* ---------- reading the trade cart ----------
     A shipment marked as added stays marked forever unless we check. If the
     buyer empties the cart, or checks out, those units are no longer there and
     warning about duplicating them is simply wrong.

     SparkLayer's cart shape is not documented, so this is tolerant about where
     the lines live and returns null when it cannot recognise them. null means
     "unknown", and every caller then leaves the workbook alone — a stale
     warning is a much smaller problem than silently clearing a real one. */

  function readCartSkus(cart) {
    if (!cart) return null;
    var lines = cart.products || cart.items || cart.lines || cart.orderLines;
    if (!Array.isArray(lines)) return null;

    var found = {};
    lines.forEach(function (line) {
      if (!line) return;
      var sku = line.sku || line.SKU || (line.variant && line.variant.sku);
      if (sku) found[String(sku)] = true;
    });
    return found;
  }

  /* ---------- money ---------- */

  function money(amount, currency, locale) {
    try {
      return new Intl.NumberFormat(locale || 'en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount);
    } catch (e) {
      return '$' + Number(amount).toFixed(2);
    }
  }

  /* ---------- the builder UI ---------- */

  function Builder(root) {
    this.root = root;
    this.customerId = root.dataset.customerId || '';
    this.quickOrderUrl = root.dataset.quickOrderUrl || '/pages/wholesale-quick-order';
    this.wb = Workbook.load(this.customerId);

    this.tabs = root.querySelector('[data-b2bob-tabs]');
    this.tray = root.querySelector('[data-b2bob-tray]');
    this.grid = root.querySelector('[data-b2bob-grid]');
    this.bar = root.querySelector('[data-b2bob-bar]');
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-b2bob-item]'));

    this.currency = root.dataset.currency;
    this.locale = root.dataset.locale;
    this.filters = { term: '', family: '', colour: '', range: '' };

    this.visit = visitId();
    this.orders = this.readOrders();
    this.reconcileOrders();
    this.retireOrdered();

    this.buildFacets();
    this.bind();
    this.render();
    this.initPricing();
    this.initCartState();
  }

  /* ---------- proof of checkout ----------
     A cart cannot tell a finished order from an emptied basket, so nothing the
     cart says is evidence that a shipment was actually placed. The customer's
     own order history can: the section renders the last 20 orders as SKUs and
     timestamps, and a shipment is only called "ordered" when an order raised
     after it went to the cart accounts for every SKU it contained.

     The match is stamped into the workbook, so the badge survives that order
     dropping out of the 20-order window. */

  Builder.prototype.readOrders = function () {
    var el = this.root.querySelector('[data-b2bob-orders]');
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  /* Returns the most recent order accounting for the shipment, or null.
     Every submitted SKU must be present — across orders raised since the
     shipment went to the cart, since a buyer can split one basket over two
     checkouts. Requiring all of them makes a false "ordered" very unlikely,
     and the cost of a miss is only that the tab stays editable. */
  Builder.prototype.shipmentOrdered = function (s) {
    if (!s.submittedAt) return null;
    if (!s.submittedSkus || !s.submittedSkus.length) return null;
    if (!this.orders.length) return null;

    // Sixty seconds of slack: the shop's clock and the browser's need not agree.
    var since = (Date.parse(s.submittedAt) || 0) / 1000 - 60;

    var seen = {};
    var newest = null;
    this.orders.forEach(function (o) {
      if (!o || typeof o.at !== 'number' || o.at < since) return;
      (o.skus || []).forEach(function (sku) { if (sku) seen[String(sku)] = true; });
      if (!newest || o.at > newest.at) newest = o;
    });
    if (!newest) return null;

    var complete = s.submittedSkus.every(function (sku) { return !!seen[sku]; });
    return complete ? newest : null;
  };

  Builder.prototype.reconcileOrders = function () {
    var self = this;
    var changed = false;

    this.wb.shipments.forEach(function (s) {
      if (s.orderedAt) return;
      var order = self.shipmentOrdered(s);
      if (!order) return;
      s.orderedAt = new Date((order.at || 0) * 1000).toISOString();
      s.orderName = order.name || '';
      changed = true;
    });

    if (changed) this.persist();
  };

  /* A shipment that became a real order has done its job. It shows as
     "ordered" for the rest of the visit that spotted it — so the buyer sees
     the outcome rather than a tab silently disappearing under them — and is
     gone the next time they open the builder. The order itself lives in their
     account; the workbook is a working area, not an order history. */
  Builder.prototype.retireOrdered = function () {
    var visit = this.visit;
    var changed = false;

    var kept = this.wb.shipments.filter(function (s) {
      var done = s.orderedAt && s.orderedSession && s.orderedSession !== visit;
      if (done) changed = true;
      return !done;
    });

    // There is always at least one shipment to work in.
    if (!kept.length) kept = [Workbook.newShipment('Shipment 1')];

    kept.forEach(function (s) {
      if (!s.orderedAt || s.orderedSession) return;
      s.orderedSession = visit;
      changed = true;
    });

    if (!changed) return;

    this.wb.shipments = kept;
    if (!Workbook.find(this.wb, this.wb.activeId)) this.wb.activeId = kept[0].id;
    this.persist();
  };

  /* 'ordered' — matched to a real order, and it stays that way.
     'cart'    — the units are sitting in the trade cart right now.
     'open'    — still a plan, however many times it has been to the cart. */
  Builder.prototype.shipmentState = function (s) {
    if (s.orderedAt) return 'ordered';
    if (this.shipmentInCart(s)) return 'cart';
    return 'open';
  };

  /* Reconcile the workbook against what is actually in the trade cart. Runs
     once on load — the buyer may have emptied the cart, or checked out, since
     they were last here. */
  Builder.prototype.initCartState = function () {
    var self = this;
    var tries = 0;

    var attempt = function () {
      if (!window.spark || typeof window.spark.getCart !== 'function') {
        if (tries++ > 40) return;
        return void setTimeout(attempt, 500);
      }

      Promise.resolve(window.spark.getCart())
        .then(function (cart) {
          var skus = readCartSkus(cart);
          if (!skus) return;
          self.cartSkus = skus;

          /* Anything still flagged as sent but neither ordered nor in the cart
             is a leftover, not a fact — including flags written before we
             recorded SKUs, which can never be checked and so must not be
             allowed to sit there claiming something we cannot support. */
          var changed = false;
          self.wb.shipments.forEach(function (s) {
            if (!s.submittedAt || s.orderedAt) return;
            if (self.shipmentInCart(s)) return;
            s.submittedAt = null;
            s.submittedSkus = [];
            changed = true;
          });

          if (changed) self.persist();
          self.render();
        })
        .catch(function () { /* cart unreadable — leave every flag as it was */ });
    };

    attempt();
  };

  Builder.prototype.shipmentInCart = function (shipment) {
    var skus = this.cartSkus;
    if (!skus || !shipment.submittedSkus) return false;
    return shipment.submittedSkus.some(function (sku) { return !!skus[sku]; });
  };

  /* ---------- Trade pricing ----------
     Same engine the size grid uses, so a style shows the same figure on both
     steps. One call per style using its first variant: on this catalogue a
     style is one price across its whole size run.

     There is deliberately no RRP fallback. If SparkLayer never loads, the line
     stays empty — a trade buyer seeing retail prices is worse than seeing
     none. */

  Builder.prototype.sparkPricingAvailable = function () {
    return !!(window.spark && typeof window.spark.calculatePricingForVariant === 'function');
  };

  Builder.prototype.priceItem = function (el) {
    var self = this;
    var sku = (el.dataset.sku || '').trim();
    var slot = el.querySelector('[data-b2bob-cost]');
    if (!slot || !sku) return Promise.resolve();

    el.classList.add('is-costing');

    return Promise.resolve()
      .then(function () {
        return window.spark.calculatePricingForVariant(el.dataset.productId, sku, 1);
      })
      .then(function (res) {
        if (!res || typeof res.unitPrice !== 'number') return;
        if (res.currencyCode) self.currency = res.currencyCode.toUpperCase();
        slot.textContent = money(res.unitPrice, self.currency, self.locale);
      })
      .catch(function () { /* leave the line blank rather than guess */ })
      .finally(function () { el.classList.remove('is-costing'); });
  };

  Builder.prototype.initPricing = function () {
    var self = this;
    if (this.root.dataset.showPrices !== 'true') return;
    if (!this.root.querySelector('[data-b2bob-cost]')) return;

    var tries = 0;
    var attempt = function () {
      if (!self.sparkPricingAvailable()) {
        // SparkLayer boots asynchronously; give it a reasonable window.
        if (tries++ > 40) return;
        return void setTimeout(attempt, 500);
      }

      // Four in flight at a time. Sequential is far too slow over a full
      // catalogue, and all-at-once hammers the SDK with hundreds of calls.
      var queue = self.items.slice();
      var CONCURRENCY = 4;

      var next = function () {
        var el = queue.shift();
        if (!el) return Promise.resolve();
        return self.priceItem(el).then(next);
      };

      var workers = [];
      for (var w = 0; w < CONCURRENCY; w++) workers.push(next());
      return Promise.all(workers);
    };

    attempt();
  };

  Builder.prototype.persist = function () {
    // Recorded so the size grid can offer a way back here without the URL
    // being hard-coded in two places.
    this.wb.builderUrl = window.location.pathname;
    Workbook.save(this.customerId, this.wb);
  };

  /* Facet chips are built from the products actually on the page rather than
     hard-coded, so a new style family or colourway needs no code change. */
  Builder.prototype.buildFacets = function () {
    var families = [];
    var colours = [];

    this.items.forEach(function (el) {
      var f = el.dataset.family;
      var c = el.dataset.colour;
      if (f && families.indexOf(f) === -1) families.push(f);
      if (c && colours.indexOf(c) === -1) colours.push(c);
    });

    families.sort();
    colours.sort();

    this.renderFacet('[data-b2bob-families]', families, 'family', 'All styles');
    this.renderFacet('[data-b2bob-colours]', colours, 'colour', 'All colours');
  };

  Builder.prototype.renderFacet = function (selector, values, kind, allLabel) {
    var host = this.root.querySelector(selector);
    if (!host) return;
    host.innerHTML = '';

    var mk = function (value, label, active) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'b2bob-chip' + (active ? ' is-active' : '');
      b.setAttribute('data-b2bob-facet', kind);
      b.setAttribute('data-value', value);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
      b.textContent = label;
      return b;
    };

    host.appendChild(mk('', allLabel, true));
    values.forEach(function (v) { host.appendChild(mk(v, v, false)); });
  };

  Builder.prototype.bind = function () {
    var self = this;

    // One delegated listener: the grid can be hundreds of cards and the tabs
    // and tray are re-rendered constantly.
    this.root.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-b2bob-toggle]');
      if (toggle) {
        e.preventDefault();
        self.toggleStyle(toggle.closest('[data-b2bob-item]').dataset.productId);
        return;
      }

      var facet = e.target.closest('[data-b2bob-facet]');
      if (facet) {
        var kind = facet.getAttribute('data-b2bob-facet');
        self.filters[kind] = facet.getAttribute('data-value') || '';
        facet.parentNode.querySelectorAll('[data-b2bob-facet]').forEach(function (b) {
          var on = b === facet;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        self.applyFilters();
        return;
      }

      // Checked before the tab itself: the close control sits inside the tab
      // button, so a click on it also lands on the tab.
      var close = e.target.closest('[data-b2bob-close-shipment]');
      if (close) {
        self.closeShipment(close.getAttribute('data-b2bob-close-shipment'));
        return;
      }

      var tab = e.target.closest('[data-b2bob-tab]');
      if (tab) {
        self.wb.activeId = tab.getAttribute('data-b2bob-tab');
        self.persist();
        self.render();
        return;
      }

      if (e.target.closest('[data-b2bob-add-shipment]')) {
        var s = Workbook.newShipment(Workbook.nextName(self.wb));
        self.wb.shipments.push(s);
        self.wb.activeId = s.id;
        self.persist();
        self.render();
        return;
      }

      var del = e.target.closest('[data-b2bob-remove-shipment]');
      if (del) {
        self.removeShipment(del.getAttribute('data-b2bob-remove-shipment'));
        return;
      }

      var chipRemove = e.target.closest('[data-b2bob-tray-remove]');
      if (chipRemove) {
        self.toggleStyle(chipRemove.getAttribute('data-b2bob-tray-remove'));
        return;
      }

      if (e.target.closest('[data-b2bob-select-visible]')) {
        self.selectVisible();
        return;
      }

      if (e.target.closest('[data-b2bob-clear-shipment]')) {
        Workbook.active(self.wb).styles = [];
        self.persist();
        self.render();
        return;
      }

      var view = e.target.closest('[data-b2bob-view]');
      if (view) {
        var mode = view.getAttribute('data-b2bob-view');
        self.grid.classList.toggle('b2bob__grid--list', mode === 'list');
        self.root.querySelectorAll('[data-b2bob-view]').forEach(function (b) {
          b.classList.toggle('is-active', b === view);
        });
      }
    });

    // The close control is a span, so it needs its keyboard activation wired
    // up by hand to stay reachable without a mouse.
    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var close = e.target.closest('[data-b2bob-close-shipment]');
      if (!close) return;
      e.preventDefault();
      self.closeShipment(close.getAttribute('data-b2bob-close-shipment'));
    });

    var search = this.root.querySelector('[data-b2bob-search]');
    if (search) {
      search.addEventListener('input', function () {
        self.filters.term = search.value.trim().toLowerCase();
        self.applyFilters();
      });
    }

    // Shipment name and dates write straight through to the workbook.
    this.root.addEventListener('input', function (e) {
      var field = e.target.closest('[data-b2bob-field]');
      if (!field) return;
      var shipment = Workbook.active(self.wb);
      shipment[field.getAttribute('data-b2bob-field')] = field.value;
      self.persist();
      if (field.getAttribute('data-b2bob-field') === 'name') self.renderTabs();
    });

    var go = this.root.querySelector('[data-b2bob-go]');
    if (go) {
      go.addEventListener('click', function () {
        var shipment = Workbook.active(self.wb);
        if (!shipment.styles.length) return;
        self.persist();
        window.location.href = self.quickOrderUrl +
          (self.quickOrderUrl.indexOf('?') === -1 ? '?' : '&') +
          'shipment=' + encodeURIComponent(shipment.id);
      });
    }
  };

  /* Closing a tab throws away whatever was picked into it, and the workbook is
     the only copy — so an empty shipment goes quietly and one with styles in it
     asks first. */
  Builder.prototype.closeShipment = function (id) {
    var shipment = Workbook.find(this.wb, id);
    if (!shipment) return;

    var n = shipment.styles.length;
    if (n) {
      var msg = 'Remove ' + shipment.name + '? Its ' +
        n + (n === 1 ? ' style' : ' styles') + ' will be discarded.';
      if (!window.confirm(msg)) return;
    }

    this.removeShipment(id);
  };

  Builder.prototype.removeShipment = function (id) {
    if (this.wb.shipments.length === 1) return;
    this.wb.shipments = this.wb.shipments.filter(function (s) { return s.id !== id; });
    if (this.wb.activeId === id) this.wb.activeId = this.wb.shipments[0].id;
    this.persist();
    this.render();
  };

  Builder.prototype.toggleStyle = function (productId) {
    var shipment = Workbook.active(this.wb);
    var i = shipment.styles.indexOf(productId);
    if (i === -1) shipment.styles.push(productId);
    else shipment.styles.splice(i, 1);
    this.persist();
    this.render();
  };

  Builder.prototype.selectVisible = function () {
    var shipment = Workbook.active(this.wb);
    this.items.forEach(function (el) {
      if (el.hidden) return;
      var id = el.dataset.productId;
      if (shipment.styles.indexOf(id) === -1) shipment.styles.push(id);
    });
    this.persist();
    this.render();
  };

  Builder.prototype.applyFilters = function () {
    var f = this.filters;
    var visible = 0;

    this.items.forEach(function (el) {
      var okTerm = !f.term || (el.dataset.search || '').indexOf(f.term) !== -1;
      var okFamily = !f.family || el.dataset.family === f.family;
      var okColour = !f.colour || el.dataset.colour === f.colour;
      // Exact tag match, not substring — same rule as the size grid's chips.
      var tags = '|' + (el.dataset.tags || '') + '|';
      var okRange = !f.range || tags.indexOf('|' + f.range + '|') !== -1;

      var show = okTerm && okFamily && okColour && okRange;
      el.hidden = !show;
      if (show) visible++;
    });

    var none = this.root.querySelector('[data-b2bob-no-results]');
    if (none) none.hidden = visible !== 0;
    var count = this.root.querySelector('[data-b2bob-visible-count]');
    if (count) count.textContent = visible;
  };

  Builder.prototype.render = function () {
    this.renderTabs();
    this.renderMeta();
    this.renderTray();
    this.renderSelectionState();
    this.renderBar();
  };

  Builder.prototype.renderTabs = function () {
    if (!this.tabs) return;
    var self = this;
    this.tabs.innerHTML = '';

    this.wb.shipments.forEach(function (s) {
      var state = self.shipmentState(s);

      var tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'b2bob-tab' + (s.id === self.wb.activeId ? ' is-active' : '') +
        (state === 'ordered' ? ' is-ordered' : '') +
        (state === 'cart' ? ' is-submitted' : '');
      tab.setAttribute('data-b2bob-tab', s.id);
      if (state === 'ordered' && s.orderName) tab.title = 'Placed as order ' + s.orderName;

      var label = document.createElement('span');
      label.className = 'b2bob-tab__name';
      label.textContent = s.name;
      tab.appendChild(label);

      var meta = document.createElement('span');
      meta.className = 'b2bob-tab__count';
      // Only ever three things: a real order, units in the cart right now, or
      // a style count. There is no in-between state, because there is nothing
      // in between we can actually verify.
      meta.textContent = state === 'ordered' ? 'ordered'
        : state === 'cart' ? 'in cart'
        : String(s.styles.length);
      tab.appendChild(meta);

      /* Close control. A span rather than a button because the tab is itself a
         button and nesting one inside another is invalid HTML — hence the
         explicit role, tabindex and key handling. Hidden on the last shipment,
         since there is always one. */
      if (self.wb.shipments.length > 1) {
        var close = document.createElement('span');
        close.className = 'b2bob-tab__close';
        close.setAttribute('data-b2bob-close-shipment', s.id);
        close.setAttribute('role', 'button');
        close.setAttribute('tabindex', '0');
        close.setAttribute('aria-label', 'Remove ' + s.name);
        close.title = 'Remove ' + s.name;
        close.textContent = '×';
        tab.appendChild(close);
      }

      self.tabs.appendChild(tab);
    });

    var add = document.createElement('button');
    add.type = 'button';
    add.className = 'b2bob-tab b2bob-tab--add';
    add.setAttribute('data-b2bob-add-shipment', '');
    add.setAttribute('aria-label', 'Add another shipment');
    add.textContent = '+';
    this.tabs.appendChild(add);
  };

  Builder.prototype.renderMeta = function () {
    var s = Workbook.active(this.wb);
    var set = function (root, key, value) {
      var el = root.querySelector('[data-b2bob-field="' + key + '"]');
      if (el && el.value !== value) el.value = value || '';
    };
    set(this.root, 'name', s.name);
    set(this.root, 'shipDate', s.shipDate);
    set(this.root, 'cancelDate', s.cancelDate);
    set(this.root, 'po', s.po);

    // The warning is about duplicating units that are sitting in the cart
    // right now, so it is tied to the live cart rather than to submittedAt.
    // Emptied cart, or checked out, means nothing left to duplicate.
    var note = this.root.querySelector('[data-b2bob-submitted-note]');
    if (note) {
      var inCart = this.shipmentInCart(s);
      note.hidden = !inCart;
      if (inCart) {
        note.textContent = s.name + ' is already in your trade cart. ' +
          'Adding it again will duplicate those units.';
      }
    }

    var del = this.root.querySelector('[data-b2bob-remove-shipment]');
    if (del) {
      del.setAttribute('data-b2bob-remove-shipment', s.id);
      del.hidden = this.wb.shipments.length < 2;
    }
  };

  Builder.prototype.renderTray = function () {
    if (!this.tray) return;
    var self = this;
    var s = Workbook.active(this.wb);
    this.tray.innerHTML = '';

    if (!s.styles.length) {
      var empty = document.createElement('p');
      empty.className = 'b2bob__tray-empty';
      empty.textContent = 'No styles yet. Pick them from the catalogue below.';
      this.tray.appendChild(empty);
      return;
    }

    s.styles.forEach(function (id) {
      var src = self.items.filter(function (el) { return el.dataset.productId === id; })[0];
      if (!src) return;

      var chip = document.createElement('div');
      chip.className = 'b2bob-tray__item';

      var img = src.querySelector('img');
      if (img) {
        var thumb = document.createElement('img');
        thumb.src = img.getAttribute('src');
        thumb.alt = '';
        thumb.className = 'b2bob-tray__img';
        thumb.loading = 'lazy';
        chip.appendChild(thumb);
      }

      var name = document.createElement('span');
      name.className = 'b2bob-tray__name';
      name.textContent = src.dataset.title || '';
      chip.appendChild(name);

      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'b2bob-tray__remove';
      x.setAttribute('data-b2bob-tray-remove', id);
      x.setAttribute('aria-label', 'Remove ' + (src.dataset.title || 'style'));
      x.textContent = '×';
      chip.appendChild(x);

      self.tray.appendChild(chip);
    });
  };

  Builder.prototype.renderSelectionState = function () {
    var s = Workbook.active(this.wb);
    this.items.forEach(function (el) {
      var on = s.styles.indexOf(el.dataset.productId) !== -1;
      el.classList.toggle('is-selected', on);
      var btn = el.querySelector('[data-b2bob-toggle]');
      if (btn) {
        btn.textContent = on ? 'Added' : 'Add';
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    });
  };

  Builder.prototype.renderBar = function () {
    if (!this.bar) return;
    var s = Workbook.active(this.wb);
    var n = s.styles.length;

    var count = this.bar.querySelector('[data-b2bob-count]');
    if (count) {
      count.textContent = n + (n === 1 ? ' style' : ' styles') + ' in ' + s.name;
    }

    var go = this.bar.querySelector('[data-b2bob-go]');
    if (go) go.disabled = n === 0;

    var clear = this.bar.querySelector('[data-b2bob-clear-shipment]');
    if (clear) clear.disabled = n === 0;
  };

  /* ---------- boot ---------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-b2bob-root]').forEach(function (root) {
      if (root.dataset.b2bobReady === '1') return;
      root.dataset.b2bobReady = '1';
      new Builder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();